import axiosInstance from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import { PhoneNumber, ApiResponse, PaginatedResponse } from 'types';
import { mockPhones } from 'mocks/mockServer';

const useMock = process.env.REACT_APP_USE_MOCK === 'true';

export const phonesService = {
  async getPhoneNumbers(params?: any): Promise<ApiResponse<PaginatedResponse<PhoneNumber>>> {
    if (useMock) {
      return mockPhones.getPhoneNumbers(params);
    }

    // Convert frontend 1-based page to backend 0-based page
    const apiParams: any = { ...params };
    if (params?.page !== undefined) {
      apiParams.page = Math.max(0, params.page - 1);
    }

    try {
      const response = await axiosInstance.get<ApiResponse<PaginatedResponse<PhoneNumber>>>(
        API_ENDPOINTS.PHONE_NUMBERS,
        { params: apiParams }
      );

      // API returns wrapped response: { success, message, data }
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch phone numbers'
      };
    }
  },

  async createPhoneNumber(data: Omit<PhoneNumber, 'id'>, organizationId?: string): Promise<ApiResponse<PhoneNumber>> {
    if (useMock) {
      return mockPhones.createPhoneNumber(data);
    }

    try {
      const params = organizationId ? { organizationId } : {};
      const response = await axiosInstance.post<ApiResponse<PhoneNumber>>(
        API_ENDPOINTS.PHONE_NUMBERS,
        data,
        { params }
      );

      // API returns wrapped response: { success, message, data }
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create phone number'
      };
    }
  },

  async updatePhoneNumber(id: number, data: Partial<PhoneNumber>, organizationId?: string): Promise<ApiResponse<PhoneNumber>> {
    if (useMock) {
      return mockPhones.updatePhoneNumber(id, data);
    }

    try {
      const params = organizationId ? { organizationId } : {};
      const response = await axiosInstance.put<ApiResponse<PhoneNumber>>(
        API_ENDPOINTS.PHONE_NUMBER_DETAIL(id),
        data,
        { params }
      );

      // API returns wrapped response: { success, message, data }
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update phone number'
      };
    }
  },

  async deletePhoneNumber(id: number, organizationId?: string): Promise<ApiResponse<void>> {
    if (useMock) {
      return mockPhones.deletePhoneNumber(id);
    }

    try {
      const params = organizationId ? { organizationId } : {};
      const response = await axiosInstance.delete<ApiResponse<void>>(
        API_ENDPOINTS.PHONE_NUMBER_DETAIL(id),
        { params }
      );

      // API returns wrapped response: { success, message, data }
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete phone number'
      };
    }
  }
};